/**
 * Exit conference summary generator.
 *
 * Aggregates findings by CfC condition, severity-ranks them, includes
 * pattern analysis and condition-level alerts. Output is structured
 * for rendering in SurveyExitSummary or PDF export.
 */

import type { SurveyDomain, SeverityLevel } from './services/survey'
import {
  detectPatterns,
  detectConditionLevel,
  getCfcConditions,
  type AnalyzableFinding,
  type PatternAlert,
  type ConditionAlert,
} from './findingAnalysis'

// ── Types ───────────────────────────────────────────────────────────────────

export interface FindingSummaryItem {
  finding_id: string
  domain: SurveyDomain
  standard_ref: string
  severity: SeverityLevel
  observation: string
  area: string
  photo_ids: string[]
}

export interface CfcGroup {
  cfcSection: string
  cfcTitle: string
  domain: SurveyDomain
  findings: FindingSummaryItem[]
  /** Highest severity in the group */
  maxSeverity: SeverityLevel
}

export interface ExitSummary {
  /** Facility name */
  facilityName: string
  /** Survey date range */
  surveyDate: string
  /** Accreditation framework used */
  framework: string
  /** Total counts */
  totalFindings: number
  totalPhotos: number
  totalDocuments: number
  /** Findings grouped by CfC condition, severity-ranked */
  cfcGroups: CfcGroup[]
  /** Ungrouped findings (no matching CfC) */
  ungroupedFindings: FindingSummaryItem[]
  /** Pattern analysis */
  patterns: PatternAlert[]
  /** Condition-level alerts */
  conditionAlerts: ConditionAlert[]
  /** Domain coverage summary */
  domainCoverage: { domain: SurveyDomain; count: number }[]
  /** Severity distribution */
  severityDistribution: { severity: SeverityLevel; label: string; count: number }[]
}

// ── Generator ───────────────────────────────────────────────────────────────

export function generateExitSummary(
  findings: FindingSummaryItem[],
  options: {
    facilityName: string
    surveyDate: string
    framework: string
    totalPhotos: number
    totalDocuments: number
  },
): ExitSummary {
  const analyzable: AnalyzableFinding[] = findings.map((f) => ({
    finding_id: f.finding_id,
    domain: f.domain,
    standard_ref: f.standard_ref,
    severity: f.severity,
    observation: f.observation,
    area: f.area,
  }))

  // Group by CfC
  const cfcConditions = getCfcConditions()
  const cfcGroupMap = new Map<string, CfcGroup>()
  const grouped = new Set<string>()

  for (const finding of findings) {
    const ref = finding.standard_ref.trim()
    for (const cfc of cfcConditions) {
      if (cfc.standardPrefixes.some((prefix) => ref.startsWith(prefix))) {
        const key = cfc.cfcSection
        const existing = cfcGroupMap.get(key)
        if (existing) {
          existing.findings.push(finding)
          if (finding.severity > existing.maxSeverity) {
            existing.maxSeverity = finding.severity as SeverityLevel
          }
        } else {
          cfcGroupMap.set(key, {
            cfcSection: cfc.cfcSection,
            cfcTitle: cfc.cfcTitle,
            domain: cfc.domain,
            findings: [finding],
            maxSeverity: finding.severity,
          })
        }
        grouped.add(finding.finding_id)
        break
      }
    }
  }

  // Sort groups by max severity (high first), then by finding count
  const cfcGroups = [...cfcGroupMap.values()].sort((a, b) => {
    if (a.maxSeverity !== b.maxSeverity) return b.maxSeverity - a.maxSeverity
    return b.findings.length - a.findings.length
  })

  // Sort findings within each group by severity
  for (const group of cfcGroups) {
    group.findings.sort((a, b) => b.severity - a.severity)
  }

  const ungroupedFindings = findings
    .filter((f) => !grouped.has(f.finding_id))
    .sort((a, b) => b.severity - a.severity)

  // Domain coverage
  const domainCountMap = new Map<SurveyDomain, number>()
  for (const f of findings) {
    domainCountMap.set(f.domain, (domainCountMap.get(f.domain) ?? 0) + 1)
  }
  const domainCoverage = [...domainCountMap.entries()]
    .map(([domain, count]) => ({ domain, count }))
    .sort((a, b) => b.count - a.count)

  // Severity distribution
  const sevCounts = { 1: 0, 2: 0, 3: 0 }
  for (const f of findings) {
    if (f.severity in sevCounts) sevCounts[f.severity as 1 | 2 | 3]++
  }
  const severityDistribution = [
    { severity: 3 as SeverityLevel, label: 'High', count: sevCounts[3] },
    { severity: 2 as SeverityLevel, label: 'Moderate', count: sevCounts[2] },
    { severity: 1 as SeverityLevel, label: 'Low', count: sevCounts[1] },
  ]

  return {
    facilityName: options.facilityName,
    surveyDate: options.surveyDate,
    framework: options.framework,
    totalFindings: findings.length,
    totalPhotos: options.totalPhotos,
    totalDocuments: options.totalDocuments,
    cfcGroups,
    ungroupedFindings,
    patterns: detectPatterns(analyzable),
    conditionAlerts: detectConditionLevel(analyzable),
    domainCoverage,
    severityDistribution,
  }
}
